import renderSelect from 'components/FormControls/select'
import renderInputField from 'components/FormControls/textInput'
import { Button } from 'gu-ui-kit'
import pick from 'lodash/pick'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, getFormValues, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import validationSchema from 'modules/Forms/Referral/PayoutProfileCreate/validationSchema'
import { AVAILABLE_CURRENCIES, PAYOUT_TYPES, WALLET_PREFIX } from 'constants/app-config'

let PayoutProfileCreateForm = ({
  profileId = undefined,
  handleSubmit,
  submitting,
  onClose = () => {},
  form,
  anyTouched,
}) => {
  const [walletPrefix, setWalletPrefix] = useState('')
  const { t } = useTranslation()
  const selector = getFormValues(form)

  const {
    formValues: { type },
    registeredFields,
  } = useSelector((state) => ({
    formValues: selector(state),
    registeredFields: state['form'][form].registeredFields,
  }))

  const [availableOptions, setAvailableOptions] = useState([])

  useEffect(() => {
    setWalletPrefix(WALLET_PREFIX[type] || WALLET_PREFIX['any'])

    const currList = AVAILABLE_CURRENCIES[type] || AVAILABLE_CURRENCIES['any']
    setAvailableOptions(
      currList.map((curr) => ({
        value: curr,
        label: t(`referral::payout_profiles_new.fields.currency.options.${curr.toLowerCase()}`),
      })),
    )
  }, [type])

  const submit = useCallback(
    (values, dispatch) => {
      const registerFieldsNames = Object.keys(registeredFields)
      const regValues = pick(values, [...registerFieldsNames])

      return new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: 'REFERRAL/PAYOUT_PROFILE_CREATE',
          payload: regValues,
          formName: form,
          profileId,
        })
      }).then(onClose)
    },
    [registeredFields, form],
  )

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className={'margin-bottom-16'}>
        <Field
          hidden={!!profileId}
          label={t('referral::payout_profiles_new.fields.type.label')}
          name="type"
          component={renderSelect}
          fullWidth
          t={t}
          showEmptyOption={false}
          emptyOptionLabel={t('common::select_empty_option')}
          clearOptionLabel={t('common::select_clear_option')}
          options={PAYOUT_TYPES.map((type) => ({
            value: type,
            label: t(`referral::payout_profiles_new.fields.type.options.${type}`),
          }))}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Field
          name="name"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('referral::payout_profiles_new.fields.name.label')}
          t={t}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Field
          normalize={(val) => (val ? walletPrefix + val : '')}
          format={(val) =>
            val
              .replace(new RegExp(`/^${walletPrefix}/`), '')
              .replace(/\D/g, '')
              .slice(0, 8)
          }
          prefix={walletPrefix}
          name="wallet"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('referral::payout_profiles_new.fields.wallet.label')}
          t={t}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Field
          label={t('referral::payout_profiles_new.fields.currency.label')}
          name="currency"
          component={renderSelect}
          fullWidth
          t={t}
          showEmptyOption={false}
          options={availableOptions}
        />
      </div>

      <div className={formStyles['formControl__action-buttons']}>
        <Button
          disabled={submitting}
          onClick={onClose}
          variant={'ghost'}
          grey
          size="l"
          type="button"
        >
          {t('common::buttons.cancel')}
        </Button>
        <Button
          disabled={!anyTouched || submitting}
          size="l"
          variant="primary"
          type="submit"
          bgColor={'Label-GU-Primary'}
        >
          {t(`common::buttons.${profileId ? 'save' : 'create'}`)}
        </Button>
      </div>
    </form>
  )
}

PayoutProfileCreateForm = reduxForm({
  form: 'PayoutProfileCreateForm',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  forceUnregisterOnUnmount: true,
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema()),
  initialValues: {
    type: 'capitalist',
    name: '',
    // contacts: {
    //   country: '',
    //   city: undefined,
    //   address: '',
    //   zip_code: undefined,
    // },
    currency: 'RUB',
    wallet: '',
  },
})(PayoutProfileCreateForm)

PayoutProfileCreateForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  form: PropTypes.string,
  anyTouched: PropTypes.bool,
  change: PropTypes.func,
  profileId: PropTypes.string,
}

export default PayoutProfileCreateForm
